import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import Select, { createFilter } from "react-select"
import styled from "styled-components"

import { STATES } from "~/utilities/constants"

const FILTER_CONFIG = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: "any",
}

const MENU_MIN_WIDTH = 240
const VIEWPORT_PADDING = 8

/** Clamp menu left so it stays on screen on small viewports (e.g. mobile) */
function clampMenuPortalPosition(provided) {
  if (typeof window === "undefined") return { ...provided, zIndex: 9999 }
  const left = typeof provided.left === "number" ? provided.left : parseFloat(String(provided.left), 10) || 0
  const maxLeft = window.innerWidth - MENU_MIN_WIDTH - VIEWPORT_PADDING
  const clampedLeft = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft))
  return { ...provided, zIndex: 9999, left: clampedLeft }
}

const Wrap = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
`

const defaultBuildOptions = () => {
  const FIRST = ["NY", "NJ", "CT", "CA"]
  const rest = Object.keys(STATES).filter((k) => !FIRST.includes(k))
  const build = (shortName) => ({ value: shortName, label: STATES[shortName] })
  return [...FIRST.map(build), ...rest.map(build)]
}

/**
 * Minimal state dropdown for the license+state combo.
 * Uses react-select directly (no Form Dropdown) so the control and menu always render.
 * Shows 2-letter code; click opens menu (with optional search via isSearchable).
 */
function EmbeddedStateDropdown({ selectedState, onChange, buildOptions = defaultBuildOptions }) {
  const [filter, setFilter] = useState("")
  const options = useMemo(() => buildOptions(), [buildOptions])
  const shortName = (typeof selectedState === "string" ? selectedState : selectedState?.shortName) || "NY"
  const value = useMemo(
    () => options.find((o) => o.value === shortName) || { value: shortName, label: STATES[shortName] || shortName },
    [options, shortName]
  )

  const handleChange = (opt) => {
    if (opt) {
      setFilter("")
      onChange(opt)
    }
  }

  return (
    <Wrap>
      <Select
        value={value}
        options={options}
        onChange={handleChange}
        onInputChange={setFilter}
        inputValue={filter}
        isSearchable
        isClearable={false}
        blurInputOnSelect
        maxMenuHeight={220}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        classNamePrefix="react-select"
        filterOption={createFilter(FILTER_CONFIG)}
        styles={{
          container: (base) => ({
            ...base,
            height: "100%",
            minHeight: 0,
            maxHeight: 70,
            overflow: "hidden",
          }),
          control: (base) => ({
            ...base,
            border: "none",
            borderRadius: "0 4px 4px 0",
            boxShadow: "none",
            minHeight: 70,
            height: 70,
            cursor: "pointer",
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "0 0 0 6px",
          }),
          singleValue: (base) => ({
            ...base,
            margin: 0,
          }),
          indicatorSeparator: () => ({ display: "none" }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "0 6px 0 0",
          }),
          menu: (base) => ({
            ...base,
            minWidth: MENU_MIN_WIDTH,
            maxWidth: typeof window !== "undefined" ? Math.min(300, window.innerWidth - VIEWPORT_PADDING * 2) : 300,
            zIndex: 9999,
          }),
          menuPortal: clampMenuPortalPosition,
        }}
        components={{
          SingleValue: ({ data }) => <span>{data.value}</span>,
        }}
      />
    </Wrap>
  )
}

EmbeddedStateDropdown.propTypes = {
  selectedState: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ shortName: PropTypes.string }),
  ]),
  onChange: PropTypes.func.isRequired,
  buildOptions: PropTypes.func,
}

export default EmbeddedStateDropdown
